import { createContext, PropsWithChildren, useEffect, useState } from "react";
import { I18N } from "oc-I18N";
import * as Commons from "oc-commons/Commons";
import * as Dialogs from "oc-ui/dialogs/Dialogs";

declare global {
  interface Window {
    schnappschussCameraPosition: ReturnType<typeof STAGE.getCurrentCameraConfig>;
  }
}

export type TSavingState = {
  readonly active: boolean;
  readonly state: "inaktive" | "presave" | "createOrUpdate" | "saving";
};
const savingStateDefault: TSavingState = {
  active: false,
  state: "inaktive",
};

function useSaving() {
  const [savingState, setSavingState] = useState<TSavingState>(savingStateDefault);
  const save = () => {
    if (!savingState.active) {
      setSavingState({ active: true, state: "presave" });
    }
  };
  useEffect(() => {
    switch (savingState.state) {
      case "presave":
        window.schnappschussCameraPosition = STAGE.getCurrentCameraConfig();

        const showTorDialog = (tor) => {
          APP.arrangement.setSelectedObjects(tor);
          APP.arrangement.activeTabKey = "Tor";
          APP.dirty();
          Dialogs.showDialog({
            content: (
              <div className="flex-row flex-center">
                <div className="text">
                  {I18N("Bitte wählen Sie ob das Tor fertig konfiguriert ist.")}
                </div>
              </div>
            ),
            buttonAndActionList: [
              {
                label: I18N("Ja"),
                primary: true,
                action: () => {
                  tor.torIstFertig = true;
                  APP.dirty();
                  checkTore();
                },
              },
              {
                label: I18N("Nein"),
                primary: true,
                action: () => {
                  setSavingState({ active: false, state: "inaktive" });
                },
              },
            ],
          });
        };

        const checkTore = () => {
          const unfertigeTore = APP.arrangement
            .getElemente()
            .filter((e) => e.isTor && !e.torIstFertig);
          if (unfertigeTore.length > 0) {
            const tor = unfertigeTore[0];
            showTorDialog(tor);

            Commons.delayUntil(
              () => setSavingState(savingStateDefault),
              () => !APP.arrangement.getElemente().some((e) => e.isTor && !e.torIstFertig),
              5000,
              null,
              () => setSavingState(savingStateDefault)
            );
          } else {
            APP.arrangement.setSelectedObjects();
            const prepareStage = async () => {
              await Commons.sleep(300);
              if (!Commons.isEmpty(APP.settings.tenant?.KonfiguratorInfoUnten)) {
                await Dialogs.showOkPromise(APP.settings.tenant?.KonfiguratorInfoUnten);
              }
              await Commons.sleep(500);
              setSavingState({ active: true, state: "createOrUpdate" });
            };
            prepareStage();
          }
        };
        checkTore();
        break;
      case "createOrUpdate":
        STAGE.setCameraConfig(window.schnappschussCameraPosition);
        APP.save(() => setSavingState({ active: true, state: "saving" })).then(() =>
          setSavingState(savingStateDefault)
        );
        break;
    }
  }, [savingState.state]);
  return { ...savingState, save };
}

export const SavingContext = createContext<TSavingState & { save: () => void }>(null);

export const SavingContextProvider = ({ children }: PropsWithChildren<{}>) => {
  const context = useSaving();
  return <SavingContext.Provider value={{ ...context }}>{children}</SavingContext.Provider>;
};
